import SearchableSelect from './search/SearchableSelect';
import IntakeEntryDialog from './intake/IntakeEntryDialog';
import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, MenuItem, FormControl, InputLabel, Alert, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EditCategoryModal from './EditCategoryModal';
import { createTransaction, getAccounts, getCategories, getSplitTemplates } from '../services/api';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 400, md: 500 },
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  maxHeight: '90vh',
  overflowY: 'auto'
};

function AddTransactionModal({ open, handleClose, onTransactionAdded }) {
  const [gift, setGift] = useState(false);
  const [transactionType, setTransactionType] = useState('expense'); // Add this line
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [splitTemplates, setSplitTemplates] = useState([]); // New state for split templates
  const [selectedSplitTemplate, setSelectedSplitTemplate] = useState(''); // New state for selected split template
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [isEditCategoryModalOpen, setIsEditCategoryModalOpen] = useState(false);
  const [selectedCategoryToEdit, setSelectedCategoryToEdit] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const accountsRes = await getAccounts();
        setAccounts(accountsRes.data);
        const categoriesRes = await getCategories();
        setCategories(categoriesRes.data);
        const templatesRes = await getSplitTemplates();
        setSplitTemplates(templatesRes.data);
      } catch (err) {
        console.error("Failed to fetch data for transaction modal", err);
        setError("Failed to load data.");
      }
    };
    
    if (open) {
      // Set today's date as default when modal opens
      const today = new Date();
      const localDate = new Date(today.getTime() - today.getTimezoneOffset() * 60000)
        .toISOString()
        .split('T')[0];
      setTransactionDate(localDate);
      
      fetchData();
    } else {
      // Reset form when modal closes
      setAmount('');
      setDescription('');
      setTransactionDate('');
      setSelectedAccount('');
      setSelectedCategory('');
      setSelectedSplitTemplate('');
      setGift(false);
      setTransactionType('expense'); // ADD THIS LINE
    }
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    try {
      if (!selectedAccount) {
        setError('Please select an account.');
        return;
      }

      let transactionData = {
        account_id: selectedAccount,
        amount: selectedSplitTemplate ? 
          parseFloat(amount) : 
          (transactionType === 'expense' ? -Math.abs(parseFloat(amount)) : Math.abs(parseFloat(amount))),
        transaction_date: transactionDate,
        description: description,
        transaction_nature: transactionType,
        user_label: gift && (selectedSplitTemplate ? splitTemplates.find(t => t.id === selectedSplitTemplate)?.transaction_type === 'expense' : transactionType === 'expense') ? 'gift' : null,
      };

      if (selectedSplitTemplate) {
        transactionData.split_template_id = selectedSplitTemplate;
      } else {
        transactionData.category_id = selectedCategory || null;
      }

      await createTransaction(transactionData);
      onTransactionAdded();
      handleClose();
    } catch (err) {
      console.error("Failed to add transaction", err);
      setError(err.response?.data?.msg || 'Failed to add transaction');
    }
  };

  const handleOpenEditCategoryModal = () => {
    const categoryToEdit = categories.find(cat => cat.id === selectedCategory);
    if (categoryToEdit) {
      setSelectedCategoryToEdit(categoryToEdit);
      setIsEditCategoryModalOpen(true);
    }
  };

  const handleCloseEditCategoryModal = () => {
    setIsEditCategoryModalOpen(false);
    setSelectedCategoryToEdit(null);
  };

  const handleCategoryUpdated = async () => {
    // Refresh categories after update
    try {
      const categoriesRes = await getCategories();
      setCategories(categoriesRes.data);
    } catch (err) {
      console.error("Failed to refresh categories", err);
      setError("Failed to refresh categories after update.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} component="form" onSubmit={handleSubmit}>
        <Typography id="modal-modal-title" variant="h6" component="h2">
          Add New Transaction
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}
        <Button variant="outlined" fullWidth sx={{ mt: 2 }} onClick={() => setIntakeOpen(true)}>
          Fill from receipt / add to Daily Tally
        </Button>
        <FormControl fullWidth margin="normal" required>
          <InputLabel id="account-select-label">Account</InputLabel>
          <SearchableSelect
            labelId="account-select-label"
            id="account-select"
            value={selectedAccount}
            label="Account"
            onChange={(e) => {
              setSelectedAccount(e.target.value);
              setSelectedCategory(''); // Reset category when account changes
              setSelectedSplitTemplate(''); // Reset split template when account changes
            }}
          >
            {accounts.filter((account) => !account.plaid_linked).map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </SearchableSelect>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="split-template-select-label">Split Template (optional)</InputLabel>
          <SearchableSelect
            labelId="split-template-select-label"
            id="split-template-select"
            value={selectedSplitTemplate}
            label="Split Template (optional)"
            onChange={(e) => {
              setSelectedSplitTemplate(e.target.value);
              setSelectedCategory(''); // Clear category if a template is selected
              const template = splitTemplates.find(t => t.id === e.target.value);
              if (template && template.default_amount && template.transaction_type === 'income') {
                setAmount(template.default_amount);
              } else if (template && template.transaction_type === 'expense') {
                setAmount(''); // Clear amount for expense templates - user must enter
              }
            }}
            disabled={!selectedAccount} // Disable if no account is selected
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {splitTemplates.filter(template => template.account_id === selectedAccount).map((template) => (
  <MenuItem key={template.id} value={template.id}>
    {template.transaction_type === 'income' ? '💰' : '💸'} {template.name}
  </MenuItem>
))}
          </SearchableSelect>
        </FormControl>

        {!selectedSplitTemplate && (
          <FormControl fullWidth margin="normal">
            <InputLabel id="category-select-label">Category</InputLabel>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SearchableSelect
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={!selectedAccount} // Disable if no account is selected
                fullWidth
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {categories.filter(cat => cat.account_id === selectedAccount).map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </SearchableSelect>
              {selectedCategory && (
                <IconButton onClick={handleOpenEditCategoryModal} sx={{ ml: 1 }}>
                  <EditIcon />
                </IconButton>
              )}
            </Box>
          </FormControl>
        )}

{/* ADD THIS NEW SECTION - Income/Expense Toggle */}
{!selectedSplitTemplate && (
  <Box sx={{ mb: 0.5 }}>
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        variant={transactionType === 'income' ? 'contained' : 'outlined'}
        color="success"
        onClick={() => { setTransactionType('income'); setGift(false); }}
        sx={{ 
          flex: 1, 
          py: 0.5,
          minHeight: 44,
          fontSize: '0.75rem'
        }}
        startIcon={<span>💰</span>}
      >
        Income
      </Button>
      <Button
        variant={transactionType === 'expense' && !gift ? 'contained' : 'outlined'}
        color="error"
        onClick={() => { setTransactionType('expense'); setGift(false); }}
        sx={{ 
          flex: 1, 
          py: 0.5,
          minHeight: 44,
          fontSize: '0.75rem'
        }}
        startIcon={<span>💸</span>}
      >
        Expense
      </Button>
      <Button variant={gift ? "contained" : "outlined"} onClick={() => { setTransactionType("expense"); setGift(true); }} sx={{ flex: 1, minHeight: 44 }}>Gift</Button>
    </Box>
  </Box>
)}

{selectedSplitTemplate && splitTemplates.find(t => t.id === selectedSplitTemplate)?.transaction_type === 'expense' && (
  <Button variant={gift ? 'contained' : 'outlined'} onClick={() => setGift(!gift)} sx={{ minHeight: 44 }}>Mark split as Gift{gift ? ' ✓' : ''}</Button>
)}
{/* MODIFY EXISTING AMOUNT TEXTFIELD */}
<TextField
  margin="normal"
  required
  fullWidth
  label={
    selectedSplitTemplate ? 
      (splitTemplates.find(t => t.id === selectedSplitTemplate)?.transaction_type === 'expense' ? 
        "Amount Spent" : "Income Amount") : 
      (transactionType === 'expense' ? (gift ? "Gift Amount" : "Amount Spent") : "Income Amount")
  }
  type="number"
  value={amount}
  onChange={(e) => setAmount(e.target.value)}
  disabled={false}
  helperText={
    selectedSplitTemplate && splitTemplates.find(t => t.id === selectedSplitTemplate)?.transaction_type === 'expense' ? 
      "Enter the total amount you spent" : 
      (!selectedSplitTemplate && transactionType === 'expense' ? 
        "Enter positive amount (app will mark as expense)" : 
        "Enter positive amount")
  }
  inputProps={{ min: 0, step: 0.01 }}
  sx={{ 
    '& .MuiInputBase-input': { 
      fontSize: { xs: '16px', md: '14px' }
    }
  }}
/>
        <TextField
          margin="normal"
          required
          fullWidth
          label="Transaction Date"
          type="date"
          InputLabelProps={{
            shrink: true,
          }}
          value={transactionDate}
          onChange={(e) => setTransactionDate(e.target.value)}
        />
        <TextField
          margin="normal"
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          Add Transaction
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </Button>

        {selectedCategoryToEdit && (
          <EditCategoryModal
            open={isEditCategoryModalOpen}
            handleClose={handleCloseEditCategoryModal}
            category={selectedCategoryToEdit}
            onCategoryUpdated={handleCategoryUpdated}
          />
        )}
        <IntakeEntryDialog open={intakeOpen && open} defaultDestination="direct"
          initialFunding={{ account_id: selectedAccount, category_id: selectedCategory, split_template_id: selectedSplitTemplate }}
          onClose={() => setIntakeOpen(false)} onSaved={() => { onTransactionAdded(); handleClose(); }} />
      </Box>
    </Modal>
  );
}

export default AddTransactionModal;
